import { Box } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { useGetUserInfo } from "@dealer-center/ddc-pages-configuration-fetch";
import type { UserInfoSubject } from "../UserInfoSubject";
import { SPACE_SECTION, SURFACE_MUTED } from "../layoutTokens";
import { TabState } from "../TabState";
import { useUserInfoArea } from "../userInfoAreaContext";
import { ContactMethods } from "./ContactMethods";
import { GeneralInformation } from "./GeneralInformation";
import type { UserInfoEdits } from "./useUserInfoEdits";

export interface UserInformationTabProps {
  subject: UserInfoSubject;
  /**
   * Pending edits, owned by `MyInfo` rather than by this component.
   *
   * A prop and not a `useUserInfoEdits()` call in here: this component unmounts on every tab switch
   * (`MyInfo` gives inactive tabs an empty fragment), so state held locally would be discarded the
   * moment the user opened another tab — exactly what FR-009 forbids.
   */
  edits: UserInfoEdits;
}

/**
 * The default tab (FR-011): General Information then Contact Methods, both fed by `userinfo`.
 *
 * The three states FR-048 requires are delegated to `TabState`, which fixes their precedence as
 * loading → failed → empty → content. Two of them are the treatments T026 asks for, and the
 * distinction between them is the point:
 *
 * - **failed** — the read did not complete. Renders an error and **no fields at all**, so a
 *   partially-populated form is never presented as the user's record (Story 1 scenario 5).
 * - **profile not found** — the read *succeeded* and there is no profile. A separate state with its
 *   own copy, never an empty editable form: once Story 2's save is enabled, an empty form standing in
 *   for a missing record is what would turn a failed lookup into a **new** record (Edge Cases).
 *
 * A profile with a blank `userId` counts as not-found. CC9 returns `""` rather than null for unset
 * strings, so "the object exists but identifies nobody" is a real shape, and treating it as a real
 * profile would render ten empty fields — precisely the case above.
 *
 * Note that a 404 from the BFF surfaces as **failed**, not not-found: `configurationRequest` throws
 * on any non-2xx. Which of the two CC9 actually returns for an unknown user is unverified (the
 * end-to-end BFF path is deferred to T071/T074), so both states are implemented rather than one.
 */
export const UserInformationTab = ({
  subject,
  edits
}: UserInformationTabProps) => {
  const { data, isPending, isError } = useGetUserInfo({
    userId: subject.userId,
    accountId: subject.accountId
  });

  const summary = data?.summary;
  const isProfileMissing = !summary?.userId;
  // Telemetry attribution only — see `userInfoAreaContext`.
  const area = useUserInfoArea();

  return (
    <TabState
      emptyMessage={Locale.getText("USER_INFO_NOT_FOUND") as string}
      isEmpty={isProfileMissing}
      isError={isError}
      isLoading={isPending}
      loadingActionName={`Loading ${area.label} user information`}
      testId={"user-information"}
    >
      {/*
       * Rendered only once the query has succeeded with a profile, but the element is still
       * constructed on every render — hence the guard, which is what narrows `summary` for the two
       * sections rather than a cast.
       */}
      {/*
       * A rule between the sections, not just a gap. Both sections are a heading over a two-column
       * field grid, so with whitespace alone the boundary between them is indistinguishable from the
       * gap between two rows *within* a grid — the reader has to parse the headings to find where one
       * section ends. `borderTop` on the second section rather than `borderBottom` on the first, so a
       * future third section needs no change and no section ever ends in a dangling rule.
       */}
      {summary ? (
        <Box>
          <GeneralInformation edits={edits} summary={summary} />
          <Box
            sx={{
              borderTop: `1px solid ${SURFACE_MUTED}`,
              marginTop: SPACE_SECTION,
              paddingTop: SPACE_SECTION
            }}
          >
            <ContactMethods edits={edits} summary={summary} />
          </Box>
        </Box>
      ) : null}
    </TabState>
  );
};
